/**
 * 防抖函数
 * 等待指定毫秒数之后才会执行
 * 可以取消待执行的调用
 *
 * @template {(...args) => any} T
 * @param {T} fn - 要防抖的函数
 * @param {number} wait - 防抖时间（毫秒）
 * @returns {T} A debounced version of fn with a .cancel() method
 */
export function debounce(fn, wait) {
  let timeout;
  function debounced(...args) {
    clearTimeout(timeout);
    timeout = setTimeout(() => fn.apply(this, args), wait);
  }
  debounced.cancel = () => {
    clearTimeout(timeout);
  };
  return debounced;
}

/**
 * 解析整数，默认值为0
 * 处理空字符串、null、undefined等情况，返回默认值
 * @template {number|null} T
 * @param {string|number|null|undefined} value - 要解析的值
 * @param {T} defaultValue - 默认值（数字或null）
 * @returns {number|T} - 解析后的整数或默认值
 */
export function parseIntOrDefault(value, defaultValue) {
  if (value === null || value === undefined || value === '') {
    return defaultValue;
  }
  const parsed = parseInt(value.toString());
  return isNaN(parsed) ? defaultValue : parsed;
}

/**
 * 存储数据到LocalStorage
 * @param {string} key 存储键名
 * @param {any} value 存储值
 * @param {number|null} [expirationInMinutes=null] 过期时间(分钟)
 */
export function storeData (key, value, expirationInMinutes = null) {
    const dataToStore = {
        value: value,
        expiration: expirationInMinutes ? Date.now() + expirationInMinutes * 60000 : null
    };
    localStorage.setItem(key, JSON.stringify(dataToStore));
}

/**
 * 从LocalStorage读取数据
 * @param {string} key 存储键名
 * @returns {any|null} 存储的值或null
 */
export function readData (key) {
    try {
        const data = localStorage.getItem(key);
        if (!data) return null;

        const dataObj = JSON.parse(data);
        if (dataObj.expiration === null || Date.now() < dataObj.expiration) {
          return dataObj.value;
        }
        localStorage.removeItem(key);
        return null;
    } catch (error) {
        return null;
    }
}


/**
 * 内容请求
 * @param url
 * @param signal
 * @returns {Promise<Document>}
 */
export async function fetchHtml(url, signal = null) {
  const fetchOptions = signal ? { signal } : {};
  const response = await fetch(url, fetchOptions);
  if (!response.ok) {
    throw new Error(
      `HTTP error! Status: ${response.status} ${response.statusText}`,
    );
  }

  const text = await response.text();
  return new DOMParser().parseFromString(text, "text/html");
}

/**
 * 创建fetch配置对象
 * @param {string} [type='json'] 响应类型，默认json
 * * @param {Object} [config] 请求配置对象
 * @param {FetchConfig['body']} [config.body] 请求体
 * @param {FetchConfig['headers']} [config.headers] 请求头
 * @returns {RequestInit} fetch配置对象
 */
export function fetchConfig(type = 'json', config = {}) {
  const headers = { 'Content-Type': 'application/json', Accept: `application/${type}`, ...config.headers };

  if (type === 'javascript') {
    headers['X-Requested-With'] = 'XMLHttpRequest';
    delete headers['Content-Type'];
  }

  return {
    method: 'POST',
    headers: headers,
    body: config.body,
  };
}

/**
 * 等待所有动画完成，然后调用回调函数。
 * @param {Element | Element[]} elements 要等待动画完成的元素或元素数组。
 * @param {() => void} [callback] 所有动画完成后调用的回调函数。
 * @param {Object} [options] 传递给 `Element.getAnimations` 的选项对象。
 * @returns {Promise<void>} 当所有动画完成后解析的 Promise。
 */
export function onAnimationEnd(elements, callback, options = { subtree: true }) {
  const animations = Array.isArray(elements) ? elements.flatMap((element) => element.getAnimations(options)) : elements.getAnimations(options);
  const animationPromises = animations.reduce((acc, animation) => {
    if (animation.timeline instanceof DocumentTimeline) {
      acc.push(animation.finished);
    }

    return acc;
  }, []);

  return Promise.allSettled(animationPromises).then(callback);
}

/**
 * Polyfill for requestIdleCallback
 * @type {(callback: IdleRequestCallback, options?: IdleRequestOptions) => number}
 */
export const requestIdleCallback = typeof window.requestIdleCallback === 'function'
    ? (cb, opts) => window.requestIdleCallback(cb, opts)
    : (cb) => window.setTimeout(cb, 0);